<template>
  <div>
    <div
      class="w-full rounded-lg flex flex-col justify-between items-center bg-black-card relative p-2.5 border border-transparent border-solid transition-all"
      :class="{
        '!border-[#838383]': hover || focus,
        '!border-red-600': error.show
      }"
      @mousemove="() => (hover = true)"
      @mouseleave="() => (hover = false)"
    >
      <div class="w-full" @keydown.stop>
        <editor-content class="editor" :editor="editor" :spellcheck="false" />
      </div>
      <!-- <div class="w-full text-right">
        <a-button size="small" @click="sourceModal?.open?.()"
          >添加数据源</a-button
        >
      </div> -->
    </div>
    <div v-if="error.show" class="text-red-600 mt-[10px]">{{ error.msg }}</div>
    <AttrColor
      class="mt-2.5"
      :value="currentColor"
      alpha-hidden
      @change="(v:string) => editor?.view.state.selection.empty?editor.chain().focus().selectAll().setColor(v).blur().run():editor?.chain().setColor(v).run()"
    >
      <template #extra>
        <span v-if="showMixedColor" class="text-white/30">(Mixed)</span>
      </template>
    </AttrColor>
    <modal-data-source
      ref="sourceModal"
      :shape-types="['text']"
      @select="handleAdd"
    />
  </div>
</template>
<script lang="ts" setup>
import { Shape } from '@/services/shape';
import { TextString } from '@/services/shape/attributes/text';
import { useRuntimeStore } from '@/store/use-runtime-store';
import {
  compareValueStrings,
  isEmojiCharacter,
  isSameText,
  textString2tiptap,
  tiptap2textString,
  tiptap2valueStrings
} from '@/utils/richtext.util';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextStyle from '@tiptap/extension-text-style';
import { EditorContent, Node, useEditor } from '@tiptap/vue-3';
import { ref, watch } from 'vue';
import DataSourceNodeExtension from './data-source-node-extension';
import { InlineColor } from './extensions/inline-color';

const hover = ref(false);
const focus = ref(false);

const props = defineProps<{
  value: TextString[];
  disabled?: boolean;
  shape: Shape;
}>();

const emit = defineEmits<{
  (event: 'change', val: TextString[]): void;
}>();

const error = ref({
  show: false,
  msg: ''
});

const preVal = ref<TextString[]>([]);

const currentColor = ref(props.value?.[0]?.color ?? '#FFF');

const getRuntimeSize = () => useRuntimeStore().shapeState(props.shape.id);

const editor = useEditor({
  extensions: [
    Node.create({
      name: 'doc',
      topNode: true,
      content: 'block'
    }),
    Text,
    Paragraph,
    TextStyle,
    InlineColor,
    History,
    DataSourceNodeExtension
  ],
  content: textString2tiptap(props.value, getRuntimeSize()?.textStrings),
  onUpdate: () => {
    const val = tiptap2textString(
      editor.value?.getJSON() ?? { type: 'doc', content: [] }
    );
    if (!vailadeInput(val)) {
      return;
    }
    emit(
      'change',
      tiptap2textString(editor.value?.getJSON() ?? { type: 'doc', content: [] })
    );
  },
  onSelectionUpdate: (e) => {
    if (
      props.value[0]?.color &&
      !props.value.every((str) => str.color === props.value[0].color) &&
      !editor.value?.isFocused
    )
      return;
    currentColor.value =
      e.editor.state.selection.$head
        .marks()
        .find((m) => m.type.name === 'textStyle')?.attrs?.['color'] ?? '#FFF';
  },
  onFocus: () => (focus.value = true),
  onBlur: () => (focus.value = false)
});

const vailadeInput = (val: TextString[]): boolean => {
  const strs = ['%s', '%d', '%f', '%0.2f'];
  let ind = 0;
  if (val.reduce((prev, item) => prev + item.value, '').length > 80) {
    error.value = {
      show: true,
      msg: '不能超过80字符'
    };
    editor.value?.commands.setContent(
      textString2tiptap(preVal.value, getRuntimeSize()?.textStrings)
    );
  } else {
    if (
      val.some((item) =>
        strs.some((it, index) => {
          const isInclude = item.value.indexOf(it) >= 0;
          if (isInclude) {
            ind = index;
          }
          return isInclude;
        })
      )
    ) {
      error.value = {
        show: true,
        msg: `不支持${strs[ind]}的输入`
      };
    } else if (val.some((item) => isEmojiCharacter(item.value))) {
      error.value = {
        show: true,
        msg: `不支持表情符号输入`
      };
    } else {
      error.value = {
        show: false,
        msg: ''
      };
    }
    preVal.value = val;
  }

  return !error.value.show;
};

watch(
  () => ({ value: props.value, strings: getRuntimeSize()?.textStrings }),
  ({ value: val, strings }) => {
    const diffRichtext =
      val !== undefined &&
      editor.value?.getJSON() &&
      isSameText(
        [
          textString2tiptap(val),
          textString2tiptap(tiptap2textString(editor.value?.getJSON()))
        ].map((item) => JSON.stringify(item))
      );

    const diffStrings =
      val !== undefined &&
      editor.value?.getJSON() &&
      compareValueStrings(
        strings ?? [],
        tiptap2valueStrings(editor.value?.getJSON() ?? {})
      );
    const isReady = editor.value && !editor.value.isFocused;
    if ((diffRichtext || diffStrings) && isReady) {
      editor.value?.commands.setContent(textString2tiptap(val, strings));
    }
  },
  { immediate: true }
);

const sourceModal = ref();

const handleAdd = (item: DataSourceItem) => {
  editor.value
    ?.chain()
    .focus()
    .insertContent(
      `<datasource key="${item.key as string}"${
        item?.formats?.[0] && ` sourceformat="${item.formats[0]}"`
      }></datasource>`
    )
    .run();
};

const showMixedColor = ref(false);

watch(
  () => ({
    isFocused: focus.value,
    emptySelection: editor.value?.state.selection.empty ?? true,
    textStrings: getRuntimeSize()?.textStrings
  }),
  ({ isFocused, emptySelection, textStrings }) => {
    const hasMixedColor =
      new Set(textStrings?.map((i) => i.color) ?? []).size > 1;
    showMixedColor.value =
      emptySelection && hasMixedColor && !(emptySelection && isFocused);
  },
  { immediate: true }
);
</script>

<style lang="less" scoped>
:deep(.ant-input) {
  background: none;
}
:deep(.ant-input-group-addon) {
  padding: 0;
  border: none;
  font-size: 0.875rem;
}
:deep(.ant-input) {
  &:focus,
  &:hover {
    border-color: #838383;
  }
}

.underline :deep(input) {
  text-decoration: underline !important;
}
:deep(.ProseMirror) {
  &:focus {
    outline: none;
  }
  ::selection {
    background-color: rgba(255, 255, 255, 0.1);
  }
  p {
    margin: 0;
    line-height: 30px;
  }
}
</style>
@/utils/richtext.util
